"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import { Modal } from "@/components/ui";
import { LoginForm } from "./login-form";
import { RegisterForm } from "./register-form";
import { GoogleFirebaseButton } from "./google-firebase-button";
import type { SafeUser } from "@/types/auth";

// Tách thành chunk riêng (chỉ tải khi cần hỏi khối) — không gói vào chunk layout.
const GradePrompt = dynamic(
  () => import("@/features/dashboard/grade-prompt").then((m) => m.GradePrompt),
  { ssr: false }
);

type AuthMode = "login" | "register";

/** Cờ bật/tắt OAuth (tính ở server rồi truyền xuống — client không đọc được env server). */
export interface OAuthFlags {
  google: boolean; // Firebase (Google)
  facebook: boolean; // OAuth thủ công
}

interface AuthModalCtx {
  openAuth: (mode?: AuthMode) => void;
  close: () => void;
}

const Ctx = React.createContext<AuthModalCtx | null>(null);

/** Mở modal đăng nhập/đăng ký từ bất kỳ client component nào. */
export function useAuthModal(): AuthModalCtx {
  const c = React.useContext(Ctx);
  if (!c) throw new Error("useAuthModal phải dùng bên trong <AuthModalProvider>.");
  return c;
}

function FacebookIcon() {
  return (
    <svg className="size-5" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#1877F2"
        d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.24 2.68.24v2.95h-1.51c-1.49 0-1.95.92-1.95 1.87V12h3.32l-.53 3.47h-2.79v8.38A12 12 0 0 0 24 12Z"
      />
    </svg>
  );
}

/**
 * Bọc toàn app: cung cấp `useAuthModal()` + render 1 modal đăng nhập/đăng ký
 * chung. Đăng nhập/đăng ký xong → đóng modal + refresh (KHÔNG rời trang hiện tại).
 */
export function AuthModalProvider({
  oauth,
  children,
}: {
  oauth: OAuthFlags;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<AuthMode>("login");
  // Sau khi đăng nhập/đăng ký ngay trên trang: nếu chưa có khối lớp → hỏi ngay.
  const [needGrade, setNeedGrade] = React.useState(false);

  const openAuth = React.useCallback((m: AuthMode = "login") => {
    setMode(m);
    setOpen(true);
  }, []);
  const close = React.useCallback(() => setOpen(false), []);
  const onSuccess = React.useCallback((user: SafeUser) => {
    setOpen(false);
    if (user.teachingGrade == null) setNeedGrade(true);
  }, []);

  const anyOAuth = oauth.google || oauth.facebook;
  const isLogin = mode === "login";

  return (
    <Ctx.Provider value={{ openAuth, close }}>
      {children}
      <Modal
        open={open}
        onClose={close}
        size="sm"
        title={isLogin ? "Đăng nhập" : "Tạo tài khoản giáo viên"}
        description={isLogin ? "Chào mừng trở lại 👋" : "Miễn phí — mọi công cụ, một nền tảng."}
      >
        <div className="space-y-5">
          {isLogin ? (
            <LoginForm onSuccess={onSuccess} />
          ) : (
            <RegisterForm onSuccess={onSuccess} />
          )}

          {anyOAuth && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-border" />
                <span className="text-caption text-muted">hoặc tiếp tục với</span>
                <span className="h-px flex-1 bg-border" />
              </div>
              <div className="grid gap-2">
                {oauth.google && <GoogleFirebaseButton onSuccess={onSuccess} />}
                {oauth.facebook && (
                  <a
                    href="/api/auth/oauth/facebook"
                    className="flex h-11 items-center justify-center gap-3 rounded-lg border border-border-strong bg-surface text-body-sm font-semibold text-foreground transition-colors hover:bg-subtle focus-visible:outline-none focus-visible:ring focus-visible:ring-ring"
                  >
                    <FacebookIcon />
                    Đăng nhập với Facebook
                  </a>
                )}
              </div>
            </div>
          )}

          <p className="text-center text-body-sm text-muted">
            {isLogin ? (
              <>
                Chưa có tài khoản?{" "}
                <button
                  type="button"
                  onClick={() => setMode("register")}
                  className="font-medium text-primary hover:underline"
                >
                  Đăng ký miễn phí
                </button>
              </>
            ) : (
              <>
                Đã có tài khoản?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  className="font-medium text-primary hover:underline"
                >
                  Đăng nhập
                </button>
              </>
            )}
          </p>
        </div>
      </Modal>

      {/* Vừa đăng nhập/đăng ký mà chưa chọn khối → hỏi ngay tại trang hiện tại. */}
      {needGrade && <GradePrompt onDone={() => setNeedGrade(false)} />}
    </Ctx.Provider>
  );
}
